import React, { Children, isValidElement, cloneElement } from 'react'
import { Box, Skeleton, Text } from '@radix-ui/themes'
import cx from 'classnames'

export type ChartVignetteProps = {
  icon?: React.ReactElement<{ className?: string }>
  title: string
  subtitle: string
  value: React.ReactNode
  className?: string
  loading?: boolean
}

export const ChartVignette = ({
  icon,
  title,
  subtitle,
  value,
  className = '',
  loading = false
}: ChartVignetteProps) => (
  <Box className={className}>
    <Skeleton loading={loading}>
      <Text size={'3'} weight={'medium'}>
        {title}{' '}
        {icon && isValidElement(icon)
          ? React.cloneElement(icon, {
              className: cx(icon.props.className ?? '', 'inline-block mb-[2px]')
            })
          : icon}
      </Text>
    </Skeleton>
    <Skeleton loading={loading}>
      <Text size={'1'} color="gray" as="p">
        {subtitle}
      </Text>
    </Skeleton>
    <Skeleton loading={loading}>
      <Text size={'5'} weight={'bold'}>
        {value}
      </Text>
    </Skeleton>
  </Box>
)

export type ChartVignettesProps = {
  children: React.ReactNode
  className?: string
}

export const ChartVignettes = ({ children, className = '' }: ChartVignettesProps) => {
  const count = Children.count(children)
  return (
    <Box className={cx(`grid grid-cols-1 sm:grid-cols-${count} gap-4 mb-4`, className ?? '')}>
      {Children.map(children, (child, idx) => {
        if (!isValidElement<ChartVignetteProps>(child)) return child
        const isMiddle = idx > 0 && idx < count - 1

        return cloneElement(child, {
          className: cx(child.props.className ?? '', 'text-center sm:text-left', {
            'sm:mr-auto': idx === 0,
            'sm:mx-auto': isMiddle,
            'sm:ml-auto': count > 1 && idx === count - 1
          })
        })
      })}
    </Box>
  )
}
